import { create, type StoreApi } from "zustand";
import type { Node } from "interfaces/schemas";
import type { FetchError } from "hooks/useFetch";
import { applyPartiallyVisbileNodes, buildTree } from "utils/treeUtils";
import { useAuthStore } from "stores/authStore";
import mandatorUtils from "utils/mandators";
import type { RefObject } from "react";

type ProjectTree = Node[] | null;
type SetProjectTreeArgs = ProjectTree | ((prev: ProjectTree) => ProjectTree);

type SetProjectArgs = {
  projectName: string;
  projectMandator: number;
  units: string;
  projectNodes: Node[];
  projectId: string;
  isSameProj: boolean;
};

interface ProjectDataState {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  treeRef: RefObject<any> | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setTreeRef: (treeRef: RefObject<any> | null) => void;
  projectId: string | null;
  projectMandator: number | null;
  projectPartners: string | null;
  setProjectId: (projectId: string | null) => void;
  setProjectPartners: (partners: string | null) => void;
  projectTree: Node[] | null;
  setProjectTree: (projectTree: SetProjectTreeArgs) => void;
  units: string | null;
  setUnits: (units: string | null) => void;
  projectName: string | null;
  setProjectName: (projectName: string | null) => void;
  error: FetchError | null;
  setError: (error: FetchError | null) => void;
  isLoading: boolean;
  setIsLoading: (isLoading: boolean) => void;
  isManualRedirect: boolean;
  setIsManualRedirect: (isManualRedirect: boolean) => void;
  setProject: (args: SetProjectArgs) => void;
  reset: () => void;
  isProductChanged: boolean;
}

export const useProjectDataStore = create<ProjectDataState>()(
  (set, _get, api: StoreApi<ProjectDataState>) => ({
    treeRef: null,
    setTreeRef: (treeRef) => set({ treeRef }),
    projectId: null,
    projectMandator: null,
    projectPartners: null,
    setProjectId: (projectId) =>
      set(() => {
        if (projectId === null || projectId === undefined) {
          return {
            projectId: null,
            projectMandator: null,
            projectPartners: null,
            projectTree: null,
            units: null,
            projectName: null,
            error: null,
            isLoading: false,
            treeRef: null,
          };
        }
        return { projectId };
      }),
    setProjectPartners: (partners) => set({ projectPartners: partners }),
    projectTree: null,
    setProjectTree: (projectTree) =>
      set((state) => ({
        projectTree:
          typeof projectTree === "function"
            ? projectTree(state.projectTree)
            : projectTree,
      })),
    units: null,
    setUnits: (units) => set({ units }),
    projectName: null,
    setProjectName: (projectName) => set({ projectName }),
    error: null,
    setError: (error) => set({ error }),
    isLoading: false,
    setIsLoading: (isLoading) => set({ isLoading }),
    isManualRedirect: false,
    setIsManualRedirect: (isManualRedirect) => set({ isManualRedirect }),
    isProductChanged: false,
    setProject: ({
      projectName,
      projectNodes,
      projectMandator,
      projectId,
      units,
      isSameProj,
    }: SetProjectArgs) => {
      const tree = buildTree(projectNodes, projectId, projectName);
      applyPartiallyVisbileNodes(tree);

      const projectPartners = isSameProj
        ? getPartnersByMandatorHelper(projectMandator)
        : null;

      set({
        projectName,
        projectMandator,
        projectPartners,
        projectTree: tree,
        units,
      });
    },

    reset: () => set(api.getInitialState()),
  }),
);

const getPartnersByMandatorHelper = (mandator: number) => {
  const currentUserTenant = useAuthStore.getState().currentUser?.tenant;

  if (mandator && currentUserTenant) {
    try {
      const parsedTenant = mandatorUtils.parseCountryCode(currentUserTenant);
      const parsedMandatorKey = mandatorUtils.parseMandatorKey(mandator);
      return mandatorUtils
        .getPartnersByMandator(parsedMandatorKey, parsedTenant)
        .join(", ");
    } catch (error) {
      console.error(error);
    }
  }
  return null;
};

export const useSetProject = () =>
  useProjectDataStore((state) => state.setProject);

export const useProjectMandator = () =>
  useProjectDataStore((state) => state.projectMandator);

interface CopiedItemState {
  copiedItem: Node | null;
  setCopiedItem: (copiedItem: Node | null) => void;
  isHoveringLeftControls: boolean;
  setIsHoveringLeftControls: (hover: boolean) => void;
  reset: () => void;
}

export const useCopiedItemStore = create<CopiedItemState>()(
  (set, _get, api: StoreApi<CopiedItemState>) => ({
    copiedItem: null,
    setCopiedItem: (copiedItem) => set({ copiedItem }),
    isHoveringLeftControls: false,
    setIsHoveringLeftControls: (hover) =>
      set({ isHoveringLeftControls: hover }),
    reset: () => set(api.getInitialState()),
  }),
);

type SetShowProductPicturesArgs = boolean | ((prev: boolean) => boolean);

interface AddedProductState {
  addedProducts: string[];
  setAddedProducts: (addedProduct: string) => void;
  hasAddedNode: boolean;
  setHasAddedNode: () => void;
  reset: () => void;
}

export const useAddedProductStore = create<AddedProductState>()(
  (set, _get, api: StoreApi<AddedProductState>) => ({
    addedProducts: [],
    hasAddedNode: false,
    setAddedProducts: (newProduct) =>
      set((state) => ({
        addedProducts: [...state.addedProducts, newProduct],
      })),
    setHasAddedNode: () => set({ hasAddedNode: true }),

    reset: () => set(api.getInitialState()),
  }),
);

// controlls open and close state of the nodes within the Project Tree
interface ProjectUIState {
  updateCount: number;
  update: () => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  openMapRef: RefObject<any> | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setOpenMapRef: (openMapRef: RefObject<any> | null) => void;
  setOpenMap: (openMap: Record<string, boolean>) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getOpenMap: () => any;
  toggleNode: (nodeId: string) => void;
  showProductPictures: boolean;
  setShowProductPictures: (
    showProductPictures: SetShowProductPicturesArgs,
  ) => void;
  reset: () => void;
}

export const useProjectUIStore = create<ProjectUIState>()(
  (set, get, api: StoreApi<ProjectUIState>) => ({
    updateCount: 0,
    update: () => set((state) => ({ updateCount: state.updateCount + 1 })),
    openMapRef: null,
    setOpenMapRef: (openMapRef) => set({ openMapRef }),
    setOpenMap: (openMap) => {
      const { openMapRef } = get();
      if (!openMapRef) {
        return;
      }
      openMapRef.current = openMap;
    },
    getOpenMap: () => get().openMapRef?.current,
    toggleNode: (nodeId: string) => {
      const { openMapRef } = get();
      if (!nodeId || !openMapRef) {
        return;
      }
      if (openMapRef.current && nodeId in openMapRef.current) {
        openMapRef.current[nodeId] = !openMapRef.current[nodeId];
        return;
      }
      if (openMapRef.current !== null) {
        openMapRef.current[nodeId] = false;
      }
    },
    showProductPictures: true,
    setShowProductPictures: (showProductPictures) =>
      set((state) => ({
        showProductPictures:
          typeof showProductPictures === "function"
            ? showProductPictures(state.showProductPictures)
            : showProductPictures,
      })),
    reset: () => set(api.getInitialState()),
  }),
);
